import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './Chat.css';

const contactEmail = 'dale@chase-ai.com';

const steps = [
 {key:'workflow',prompt:'What would you like to make possible in your business?',choices:['Explore an opportunity','Improve how we work','Build a new product','Find an ongoing partner','Send Dale a message']},
 {key:'business',prompt:'Tell me a little about your business and the people this would help.'},
 {key:'currentWorkflow',prompt:'What is happening today, and what would you like to change or create?'},
 {key:'successMetric',prompt:'What would a useful outcome look like for your team or customers?'},
 {key:'tools',prompt:'Are there existing tools or information we should understand? It is fine if you are not sure yet.'},
 {key:'timeline',prompt:'Is there a particular milestone or timing Dale should know about?'},
 {key:'name',prompt:'What is your name?'},
 {key:'company',prompt:'What company or website should Dale know? You can say not applicable.'},
 {key:'email',prompt:'What email should Dale use to follow up?'}
];
const initialMessage={role:'bot',text:'Let’s explore your goals. I can help organize the context for a conversation with Dale. Please leave out confidential information. You will review your inquiry before sending it.'};

function getApiUrl(path) {
  const host = window.location.hostname;
  const localApi = `http://${host || 'localhost'}:8787${path}`;

  if (host === 'localhost' || host === '127.0.0.1') {
    return localApi;
  }

  return path;
}

function openAssistant() {
  window.dispatchEvent(new Event('open-project-assistant'));
}

function isLikelyQuestion(value) {
  const normalized = value.trim().toLowerCase();

  return normalized.endsWith('?') || /^(who|what|when|where|why|how|can|could|do|does|is|are|tell me|what kind|what services)/.test(normalized);
}

function getEmailFromText(value) {
  const match = value.match(/[^\s@]+@[^\s@]+\.[^\s@]+/);
  return match ? match[0].replace(/[.,;:!?)]$/, '') : '';
}

function getPhoneFromText(value) {
  const match = value.match(/(?:\+?1[\s.-]?)?(?:\(?\d{3}\)?[\s.-]?)\d{3}[\s.-]?\d{4}/);
  return match ? match[0].trim() : '';
}

function isContactOnlyText(value) {
  const withoutEmail = value.replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, ' ');
  const withoutPhone = withoutEmail.replace(/(?:\+?1[\s.-]?)?(?:\(?\d{3}\)?[\s.-]?)\d{3}[\s.-]?\d{4}/g, ' ');
  const withoutContactLabels = withoutPhone.replace(/\b(name|email|phone|number|company|this is|i am|i'm|my name is|from|at|reach me|contact me|call me)\b/gi, ' ');
  const remainingWords = withoutContactLabels
    .replace(/[^a-z0-9\s]/gi, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return Boolean(getEmailFromText(value) || getPhoneFromText(value)) && remainingWords.length <= 3;
}

function isDirectMessageIntent(value) {
  const normalized = value.trim().toLowerCase();

  return /\b(send|leave|write|pass|forward)\b.*\b(dale|message|note|email)\b/.test(normalized)
    || /\b(tell|ask)\s+dale\b/.test(normalized)
    || /\bdale\b.*\b(call|contact|email|message|reach out)\b/.test(normalized)
    || /\b(have|get)\s+dale\s+(call|contact|email|reach out)\b/.test(normalized)
    || /\b(contact|email|message)\s+dale\b/.test(normalized)
    || /\b(please\s+)?(call|contact|email|message)\s+me\b/.test(normalized)
    || /\breach out\s+to\s+me\b/.test(normalized)
    || /\b(just|only)\s+(want|need|like)\s+to\s+(send|leave)\s+(a\s+)?(message|note)\b/.test(normalized);
}

function isBareDirectMessageRequest(value) {
  const normalized = value.trim().toLowerCase();

  return /^(i\s+)?(just\s+|only\s+)?(want|need|would like)\s+to\s+(send|leave|write)\s+(dale\s+)?(a\s+)?(message|note)$/.test(normalized)
    || /^(send|leave|write)\s+(dale\s+)?(a\s+)?(message|note)$/.test(normalized)
    || /^(contact|email|message)\s+dale$/.test(normalized)
    || /^(have|get)\s+dale\s+(call|contact|email|reach out)(\s+to\s+me|\s+me)?$/.test(normalized);
}

function getRecommendation() {
 return 'Discuss the goal and open questions with Dale before choosing an engagement. Existing tools, discovery, implementation or ongoing help may be appropriate.';
}

function buildSummary(data) {
  const recommendation = getRecommendation(data.workflow || '');

  return [
    `Next conversation: ${recommendation}`,
    '',
    `Area of interest: ${data.workflow || 'Not provided'}`,
    `Business context: ${data.business || 'Not provided'}`,
    `Current situation: ${data.currentWorkflow || 'Not provided'}`,
    `Tools and data: ${data.tools || 'Not provided'}`,
    `Success metric: ${data.successMetric || 'Not provided'}`,
    `Timeline: ${data.timeline || 'Not provided'}`,
    '',
    `Contact: ${data.name || 'Not provided'}`,
    `Company: ${data.company || 'Not provided'}`,
    `Email: ${data.email || 'Not provided'}`
  ].join('\n');
}

function formatAiBrief(brief, answers) {
  if (!brief) {
    return buildSummary(answers);
  }

  if (brief.emailBrief) {
    return brief.emailBrief;
  }

  const clientSummary = typeof brief.clientSummary === 'string'
    ? brief.clientSummary
    : JSON.stringify(brief.clientSummary || {}, null, 2);

  return [
    `Next conversation: ${brief.recommendedPilot || getRecommendation(answers.workflow || '')}`,
    '',
    clientSummary,
    '',
    'Topics to explore:',
    ...(brief.implementationPlan || []).map((item) => `- ${item}`),
    '',
    'Risks / open questions:',
    ...(brief.risks || []).map((item) => `- ${item}`),
    '',
    `Contact: ${answers.name || 'Not provided'}`,
    `Company: ${answers.company || 'Not provided'}`,
    `Email: ${answers.email || 'Not provided'}`
  ].join('\n');
}

function getMissingDirectMessageStep(messageDetails) {
  if (!messageDetails.message) {
    return 'message';
  }

  if (!messageDetails.name) {
    return 'name';
  }

  if (!messageDetails.email && !messageDetails.phone) {
    return 'contact';
  }

  return null;
}

function getDirectMessagePrompt(step) {
  if (step === 'message') {
    return 'Sure. What would you like Dale to know?';
  }

  if (step === 'name') {
    return 'Got it. What name should Dale recognize?';
  }

  return 'What email or phone number should Dale use to reply?';
}

function buildGeneralMessageSummary(messageDetails) {
  return [
    'Website message for Dale',
    '',
    `Message: ${messageDetails.message || 'Not provided'}`,
    '',
    `Name: ${messageDetails.name || 'Not provided'}`,
    `Email: ${messageDetails.email || 'Not provided'}`,
    `Phone: ${messageDetails.phone || 'Not provided'}`,
    `Company: ${messageDetails.company || 'Not provided'}`
  ].join('\n');
}

function mergeDirectMessageDetails(currentDetails, extractedDetails, fallbackDetails) {
  return {
    message: extractedDetails.message || fallbackDetails.message || currentDetails.message || '',
    name: extractedDetails.name || fallbackDetails.name || currentDetails.name || '',
    email: extractedDetails.email || fallbackDetails.email || currentDetails.email || '',
    phone: extractedDetails.phone || fallbackDetails.phone || currentDetails.phone || '',
    company: extractedDetails.company || fallbackDetails.company || currentDetails.company || ''
  };
}

async function getSubmissionPayload(response, fallbackErrorMessage) {
  let payload = {};

  try {
    payload = await response.json();
  } catch (error) {
    throw new Error('We could not confirm delivery. Please contact Dale directly.');
  }

  if (!response.ok || payload.ok !== true) {
    throw new Error(payload.error || fallbackErrorMessage);
  }

  return payload;
}

function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [messages, setMessages] = useState([initialMessage, { role: 'bot', text: steps[0].prompt }]);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [assistantMode, setAssistantMode] = useState('project');
  const [messageStep, setMessageStep] = useState(null);
  const [directMessage, setDirectMessage] = useState({
    message: '',
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [inputValue, setInputValue] = useState('');
  const [isReadyToSend, setIsReadyToSend] = useState(false);
  const [sendStatus, setSendStatus] = useState('idle');
  const [briefStatus, setBriefStatus] = useState('idle');
  const [projectBrief, setProjectBrief] = useState(null);
  const [isThinking, setIsThinking] = useState(false);
  const transcriptRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  const currentStep = steps[stepIndex];
  const summary = useMemo(() => formatAiBrief(projectBrief, answers), [answers, projectBrief]);
  const directMessageSummary = useMemo(() => buildGeneralMessageSummary(directMessage), [directMessage]);

  const showAssistant = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    setIsClosing(false);
    setIsOpen(true);
  }, []);

  const closeAssistant = useCallback(() => {
    setIsClosing(true);

    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      closeTimeoutRef.current = null;
    }, 180);
  }, []);

  useEffect(() => {
    const open = () => showAssistant();

    window.addEventListener('open-project-assistant', open);
    window.openChaseAiAssistant = openAssistant;

    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }

      window.removeEventListener('open-project-assistant', open);
      delete window.openChaseAiAssistant;
    };
  }, [showAssistant]);

  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement;
    const close = document.querySelector('.assistant-close');
    if (close) close.focus();
    const escape = event => { if (event.key === 'Escape') closeAssistant(); };
    window.addEventListener('keydown', escape);
    return () => { window.removeEventListener('keydown', escape); if (previous && previous.focus) previous.focus(); };
  }, [isOpen, closeAssistant]);

  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [messages, sendStatus, isThinking]);

  const addMessage = (message) => {
    setMessages((existing) => [...existing, message]);
  };

  const getAssistantTurn = async ({ nextAnswers, lastAnswer, nextPrompt }) => {
    const response = await fetch(getApiUrl('/api/assistant-turn'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        answers: nextAnswers,
        lastAnswer,
        nextPrompt
      })
    });
    const payload = await response.json();

    return payload.message || `Got it. ${nextPrompt}`;
  };

  const answerQuestion = async ({ question, currentPrompt }) => {
    const response = await fetch(getApiUrl('/api/assistant-question'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        question,
        answers,
        currentPrompt
      })
    });
    const payload = await response.json();

    return payload.message || `I can answer questions about Dale, Chase AI services, and project fit. ${currentPrompt}`;
  };

  const getProjectBrief = async (nextAnswers) => {
    const response = await fetch(getApiUrl('/api/project-brief'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        answers: nextAnswers
      })
    });
    const payload = await response.json();

    return payload.brief;
  };

  const submitProjectInquiry = async ({ currentAnswers, brief }) => {
    const response = await fetch(getApiUrl('/api/project-inquiry'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        answers: currentAnswers,
        brief
      })
    });

    await getSubmissionPayload(response, 'Form submission failed');
  };

  const submitGeneralMessage = async (messageDetails) => {
    const response = await fetch(getApiUrl('/api/general-message'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        message: messageDetails.message,
        name: messageDetails.name,
        email: messageDetails.email,
        phone: messageDetails.phone,
        company: messageDetails.company
      })
    });

    await getSubmissionPayload(response, 'Message submission failed');
  };

  const extractDirectMessageDetails = async ({ text, currentDetails, fallbackDetails, expectedField, allowTextAsMessage }) => {
    try {
      const response = await fetch(getApiUrl('/api/message-extract'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text,
          currentDetails,
          expectedField,
          allowTextAsMessage
        })
      });
      const payload = await response.json();

      return mergeDirectMessageDetails(
        currentDetails,
        payload.details || {},
        fallbackDetails
      );
    } catch (error) {
      return mergeDirectMessageDetails(currentDetails, {}, fallbackDetails);
    }
  };

  const continueDirectMessage = async (messageDetails) => {
    const nextStep = getMissingDirectMessageStep(messageDetails);
    setDirectMessage(messageDetails);

    if (nextStep) {
      setMessageStep(nextStep);
      addMessage({ role: 'bot', text: getDirectMessagePrompt(nextStep) });
      return;
    }

    setMessageStep(null);
    setIsReadyToSend(true);
    setSendStatus('idle');
    addMessage({ role: 'bot', text: 'Please review your message below, then choose Send to Dale when you are ready.' });
  };

  const startDirectMessage = async (value) => {
    const extractedEmail = getEmailFromText(value);
    const extractedPhone = getPhoneFromText(value);
    const baseDetails = {
      message: '',
      name: answers.name || '',
      email: answers.email || '',
      phone: '',
      company: answers.company || ''
    };
    const fallbackDetails = {
      message: isBareDirectMessageRequest(value) ? '' : value,
      name: answers.name || '',
      email: extractedEmail || answers.email || '',
      phone: extractedPhone,
      company: answers.company || ''
    };

    setAssistantMode('message');
    setBriefStatus('idle');
    setSendStatus('idle');
    setInputValue('');
    addMessage({ role: 'user', text: value });
    setIsThinking(true);

    try {
      const nextMessage = await extractDirectMessageDetails({
        text: value,
        currentDetails: baseDetails,
        fallbackDetails,
        expectedField: 'message',
        allowTextAsMessage: !isBareDirectMessageRequest(value) && !isContactOnlyText(value)
      });
      await continueDirectMessage(nextMessage);
    } finally {
      setIsThinking(false);
    }
  };

  const handleDirectMessageAnswer = async (value) => {
    const extractedEmail = getEmailFromText(value);
    const extractedPhone = getPhoneFromText(value);
    const fallbackDetails = {
      ...directMessage
    };

    if (messageStep === 'message') {
      fallbackDetails.message = isContactOnlyText(value) ? fallbackDetails.message : value;
      fallbackDetails.email = extractedEmail || fallbackDetails.email;
      fallbackDetails.phone = extractedPhone || fallbackDetails.phone;
    } else if (messageStep === 'name') {
      fallbackDetails.name = value;
    } else if (messageStep === 'contact') {
      fallbackDetails.email = extractedEmail || fallbackDetails.email;
      fallbackDetails.phone = extractedPhone || fallbackDetails.phone;
    }

    setInputValue('');
    addMessage({ role: 'user', text: value });
    setIsThinking(true);

    try {
      const nextMessage = await extractDirectMessageDetails({
        text: value,
        currentDetails: directMessage,
        fallbackDetails,
        expectedField: messageStep,
        allowTextAsMessage: messageStep === 'message' && !isContactOnlyText(value)
      });
      await continueDirectMessage(nextMessage);
    } finally {
      setIsThinking(false);
    }
  };

  const handleAnswer = async (answer) => {
    const value = answer.trim();

    if (!value || isReadyToSend || briefStatus === 'generating') {
      return;
    }

    if (assistantMode === 'message') {
      await handleDirectMessageAnswer(value);
      return;
    }

    if (isDirectMessageIntent(value)) {
      await startDirectMessage(value);
      return;
    }

    if (isLikelyQuestion(value)) {
      addMessage({ role: 'user', text: value });
      setInputValue('');
      setIsThinking(true);

      try {
        const reply = await answerQuestion({
          question: value,
          currentPrompt: currentStep.prompt
        });
        addMessage({ role: 'bot', text: reply });
      } catch (error) {
        addMessage({
          role: 'bot',
          text: `I can answer questions about Dale, Chase AI services, and project fit. ${currentStep.prompt}`
        });
      } finally {
        setIsThinking(false);
      }

      return;
    }

    const nextAnswers = {
      ...answers,
      [currentStep.key]: value
    };
    const nextIndex = stepIndex + 1;

    setAnswers(nextAnswers);
    addMessage({ role: 'user', text: value });
    setInputValue('');

    if (nextIndex < steps.length) {
      setStepIndex(nextIndex);
      setIsThinking(true);
      try {
        const reply = await getAssistantTurn({
          nextAnswers,
          lastAnswer: value,
          nextPrompt: steps[nextIndex].prompt
        });
        addMessage({ role: 'bot', text: reply });
      } catch (error) {
        addMessage({ role: 'bot', text: `Got it. ${steps[nextIndex].prompt}` });
      } finally {
        setIsThinking(false);
      }
      return;
    }

    setIsReadyToSend(true);
    setBriefStatus('generating');
    setSendStatus('idle');
    setIsThinking(true);
    addMessage({ role: 'bot', text: 'Thanks. I have what I need. I am organizing the project details for Dale now.' });

    let brief = null;

    try {
      brief = await getProjectBrief(nextAnswers);
      setProjectBrief(brief);
      setBriefStatus('ready');
    } catch (error) {
      setProjectBrief(null);
      setBriefStatus('fallback');
    }

    setIsThinking(false);
    addMessage({role:'bot',text:'Review the details below. Nothing has been sent yet. Choose Send to Dale when you are ready.'});
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleAnswer(inputValue);
  };

  const sendSummary = async () => {
    if (isThinking || sendStatus === 'sent') return;
    setSendStatus('sending');
    setIsThinking(true);

    try {
      if (assistantMode === 'message') {
        await submitGeneralMessage(directMessage);
      } else {
        await submitProjectInquiry({ currentAnswers: answers, brief: projectBrief });
      }

      setSendStatus('sent');
      addMessage({
        role: 'bot',
        text: assistantMode === 'message'
          ? "Thanks. I sent your message to Dale. Dale will review your inquiry and follow up."
          : "Thanks. I'm passing this along to Dale with the project details. Dale will review your inquiry and follow up."
      });
    } catch (error) {
      setSendStatus('error');
      addMessage({
        role: 'bot',
        text: `I could not send the brief automatically. You can still send it to ${contactEmail} using the fallback email link below.`
      });
    } finally {
      setIsThinking(false);
    }
  };

  const mailtoHref = `mailto:${contactEmail}?subject=${encodeURIComponent(`New Chase AI project inquiry: ${answers.workflow || 'AI automation'}`)}&body=${encodeURIComponent(summary)}`;
  const directMessageMailtoHref = `mailto:${contactEmail}?subject=${encodeURIComponent(`New Chase AI website message: ${directMessage.name || 'Website visitor'}`)}&body=${encodeURIComponent(directMessageSummary)}`;

  return (
    <>
      <button
        className="assistant-launcher"
        type="button"
        onClick={showAssistant}
        aria-label="Open Chase AI project assistant"
      >
        <img src="/botlogo.png" alt="" aria-hidden="true" />
        <span>Project Assistant</span>
      </button>

      {(isOpen || isClosing) && (
        <div className={`assistant-panel${isClosing ? ' is-closing' : ''}`} role="dialog" aria-label="Chase AI project assistant">
          <div className="assistant-header">
            <div>
              <span className="assistant-kicker">Chase AI</span>
              <h2>Project Assistant</h2>
            </div>
            <button className="assistant-close" type="button" onClick={closeAssistant} aria-label="Close project assistant">
              ×
            </button>
          </div>

          <div className="assistant-transcript" ref={transcriptRef} role="log" aria-live="polite">
            {messages.map((message, index) => (
              <div className={`assistant-message ${message.role}`} key={`${message.role}-${index}`}>
                {message.text.split('\n').map((line, lineIndex) => (
                  <React.Fragment key={`${line}-${lineIndex}`}>
                    {line}
                    {lineIndex < message.text.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
            ))}
            {isThinking && (
              <div className="assistant-message bot assistant-typing" aria-label="Assistant is typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
          </div>

          {assistantMode === 'project' && !isReadyToSend && !isThinking && currentStep.choices && (
            <div className="assistant-choices">
              {currentStep.choices.map((choice) => (
                <button type="button" key={choice} onClick={() => handleAnswer(choice)}>
                  {choice}
                </button>
              ))}
            </div>
          )}

          {!isReadyToSend && (
            <form className="assistant-form" onSubmit={handleSubmit}>
              <input
                type={assistantMode === 'project' && currentStep.key === 'email' ? 'email' : 'text'}
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                placeholder={assistantMode === 'message' ? 'Type your answer' : currentStep.choices ? 'Or type your own answer' : 'Type your answer'}
                aria-label="Project assistant answer"
                required={assistantMode === 'message' || !currentStep.choices}
                disabled={isThinking}
              />
              <button type="submit" disabled={isThinking}>{isThinking ? 'Thinking' : 'Send'}</button>
            </form>
          )}

          {isReadyToSend && !isThinking && sendStatus !== 'sent' && (<div className="assistant-review" tabIndex="0" aria-label="Review your inquiry">{assistantMode === 'message' ? directMessageSummary : summary}</div>)}
          {isReadyToSend && (sendStatus === 'idle' || sendStatus === 'error') && !isThinking && (
            <div className="assistant-send-actions">
              <button
                type="button"
                onClick={sendSummary}
                disabled={sendStatus === 'sending'}
              >
                {sendStatus === 'error' ? 'Try sending again' : 'Send to Dale'}
              </button>
              <a href={assistantMode === 'message' ? directMessageMailtoHref : mailtoHref}>Open email fallback</a>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export { openAssistant };
export default Chatbot;
